import { useState, useEffect } from 'react'
import { useSelectTenant } from '~/app/services/auth/auth.hook'
import { Tenant } from '~/app/services/auth/auth.type'
import AuthLayout from '~/app/components/layouts/AuthLayout'
import Button from '~/app/components/utils/Button/Button'
import Text from '~/app/components/utils/Text/Text'

interface TenantSelectionProps {
  tenants: Tenant[]
  tempToken: string
}

export default function TenantSelection({ tenants, tempToken }: TenantSelectionProps) {
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const { mutateAsync: selectTenant, isPending } = useSelectTenant()

  // Auto-select if only one tenant
  useEffect(() => {
    if (tenants.length === 1) {
      setSelectedTenantId(tenants[0].id)
    }
  }, [tenants])

  const handleTenantSelect = async () => {
    if (!selectedTenantId) return

    // Always use the select tenant API
    await selectTenant({
      tenant_id: selectedTenantId,
      temp_token: tempToken,
    })
  }

  return (
    <AuthLayout>
      <div className="max-w-lg w-full space-y-8">
        <div className="text-center">
          <Text type="headline-4" color="800" className="mb-2">
            Select Organization
          </Text>
          <Text type="body-1" color="600">
            Choose your organization to continue to QA Genie Dashboard
          </Text>
        </div>

        <div className="space-y-3">
          <Text type="body-2" color="700" className="font-medium mb-3">
            Available Organizations ({tenants.length})
          </Text>
          {tenants.map((tenant) => (
            <div
              key={tenant.id}
              onClick={() => setSelectedTenantId(tenant.id)}
              style={{
                padding: '1rem',
                border: '2px solid',
                borderColor:
                  selectedTenantId === tenant.id ? 'var(--primary)' : 'var(--grey-200)',
                backgroundColor:
                  selectedTenantId === tenant.id ? 'var(--primary-50)' : 'transparent',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (selectedTenantId !== tenant.id) {
                  e.currentTarget.style.borderColor = 'var(--primary)'
                  e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }
              }}
              onMouseLeave={(e) => {
                if (selectedTenantId !== tenant.id) {
                  e.currentTarget.style.borderColor = 'var(--grey-200)'
                  e.currentTarget.style.boxShadow = 'none'
                }
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <Text type="title-2" color="900" className="mb-1">
                    {tenant.name}
                  </Text>
                  <Text type="body-3" color="500">
                    ID: {tenant.slug}
                  </Text>
                </div>
                <div
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor:
                      selectedTenantId === tenant.id ? 'var(--primary)' : 'var(--grey-300)',
                    backgroundColor:
                      selectedTenantId === tenant.id ? 'var(--primary)' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {selectedTenantId === tenant.id && (
                    <div
                      style={{
                        width: '0.5rem',
                        height: '0.5rem',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <Button
          onClick={handleTenantSelect}
          disabled={!selectedTenantId || isPending}
          loading={isPending}
          className="w-full"
        >
          <Text type="title-3" color="white">
            Continue to Dashboard
          </Text>
        </Button>

        {tenants.length > 1 && (
          <Text type="body-4" color="500" align="center">
            You have access to {tenants.length} organizations. Select one to continue.
          </Text>
        )}
      </div>
    </AuthLayout>
  )
}
